import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, TABLES } from '../supabase/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activeOrg, setActiveOrg] = useState(null);
  const [userOrgs, setUserOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearState = () => {
    setUser(null);
    setProfile(null);
    setActiveOrg(null);
    setUserOrgs([]);
    localStorage.removeItem('rutabaga_active_org');
  };

  const loadUserData = async (authUser) => {
    if (!authUser) {
      clearState();
      setLoading(false);
      return;
    }

    try {
      // Fetch profile
      const { data: profileData } = await supabase
        .from(TABLES.USER_PROFILES)
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();
      
      setProfile(profileData);

      // Fetch user organizations
      const { data: orgs } = await supabase
        .from(TABLES.ORG_MEMBERS)
        .select(`
          role,
          status,
          org: ${TABLES.ORGANIZATIONS} (
            id,
            name
          )
        `)
        .eq('user_id', authUser.id)
        .eq('status', 'active');

      const formattedOrgs = orgs?.map(item => ({
        ...item.org,
        role: item.role,
        status: item.status
      })) || [];

      setUserOrgs(formattedOrgs);

      const savedOrgId = localStorage.getItem('rutabaga_active_org');
      const existingOrg = formattedOrgs.find(o => o.id === savedOrgId);

      if (existingOrg) {
        setActiveOrg(existingOrg);
      } else if (formattedOrgs.length > 0) {
        setActiveOrg(formattedOrgs[0]);
        localStorage.setItem('rutabaga_active_org', formattedOrgs[0].id);
      }
      
      setUser(authUser);
    } catch (err) {
      console.error("Auth init error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession()
      .then(({ data: { session } }) => loadUserData(session?.user ?? null))
      .catch((err) => { console.error('getSession failed:', err); setLoading(false); });

    // Backstop: never let a stuck auth call (e.g. Safari Web Locks) freeze the
    // whole app on the loading spinner — fall through to the login screen.
    const backstop = setTimeout(() => setLoading(false), 6000);

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        clearState();
        setLoading(false);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        loadUserData(session?.user);
      }
    });

    return () => { clearTimeout(backstop); subscription.unsubscribe(); };
  }, []);

  const selectOrg = (org) => {
    setActiveOrg(org);
    localStorage.setItem('rutabaga_active_org', org.id);
  };

  const logout = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    clearState();
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      activeOrg, 
      userOrgs, 
      loading, 
      selectOrg,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};